'use client';

import { Button } from '../../components/button';
import { IconButton } from '../../components/icon-button';
import { SeverityIndicator } from '../../components/severity-indicator';
import { Tooltip } from '../../components/tooltip';
import {
  BodyDefault,
  HeadingItem,
  LabelDefault,
  MetaDefault,
  MonoSmall,
} from '../../components/typography';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { formatDistanceToNowStrict } from 'date-fns';
import { ArrowUpRight01Icon, Cancel01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  getActionTranslationKey,
  getSeverityFromRank,
  getSignalImportActionCounts,
  isSignalImportAction,
} from './action-display';
import type {
  ApplicationNotificationDto,
  PlatformNotificationActionType,
} from '@terra/shared-contracts-notification-center';

type ApplicationNotificationActionRowProps = {
  action: ApplicationNotificationDto;
  showBottomBorder?: boolean;
  onPrimaryAction?: ({
    actionId,
    type,
  }: {
    actionId: string;
    type: PlatformNotificationActionType;
  }) => void;
  onDismiss?: ({ actionId }: { actionId: string }) => void;
  onLinkClick?: ({
    actionId,
    type,
  }: {
    actionId: string;
    type: PlatformNotificationActionType;
  }) => void | Promise<void>;
};

export function ApplicationNotificationActionRow({
  action,
  showBottomBorder = true,
  onPrimaryAction,
  onDismiss,
  onLinkClick,
}: ApplicationNotificationActionRowProps) {
  const tWidget = useTranslations(
    'designSystem.notificationCenter.actionsWidget',
  );
  const tActions = useTranslations(
    'designSystem.notificationCenter.actionsWidget.actions',
  );
  const actionType = action.type as PlatformNotificationActionType;
  const translationKey = getActionTranslationKey({ type: actionType });
  const severity = getSeverityFromRank({
    severityRank: action.severityRank,
  });
  const providerDisplayName =
    typeof action.context?.providerDisplayName === 'string'
      ? action.context.providerDisplayName
      : undefined;
  const applicationName =
    typeof action.context?.applicationName === 'string'
      ? action.context.applicationName
      : undefined;
  const createdAtLabel = formatDistanceToNowStrict(action.createdAt, {
    addSuffix: true,
  });
  const titleLabel = tActions(`${translationKey}.title`, {
    provider: providerDisplayName ?? '',
    applicationName: applicationName ?? '',
  });
  const entityLabel =
    typeof action.context?.entityLabel === 'string'
      ? action.context.entityLabel
      : '';
  const ctaLabel = tActions(`${translationKey}.cta`);
  const handlePrimaryActionClick = () => {
    onPrimaryAction?.({ actionId: action.id, type: actionType });
  };

  const handleDismissClick = () => {
    onDismiss?.({ actionId: action.id });
  };

  if (isSignalImportAction({ type: actionType })) {
    const { importedSignalCount, failedCount } = getSignalImportActionCounts({
      context: action.context,
    });
    const descriptionParts: string[] = [];

    if (importedSignalCount > 0) {
      descriptionParts.push(
        tActions('importCompleted.signalsCreated', {
          count: importedSignalCount,
        }),
      );
    }

    if (failedCount > 0) {
      descriptionParts.push(
        tActions('importCompleted.rowsFailed', { count: failedCount }),
      );
    }

    const description =
      descriptionParts.length > 0 ? `${descriptionParts.join(', ')}.` : '';
    const handleLinkClick = () => {
      void onLinkClick?.({
        actionId: action.id,
        type: actionType,
      });
    };

    return (
      <HStack
        gap={4}
        alignItems="center"
        w="full"
        py={2}
        px={3}
        borderBottomWidth={showBottomBorder ? '1px' : '0'}
        borderBottomStyle="solid"
        borderBottomColor="border.neutral.subtle"
      >
        <VStack gap={1} alignItems="flex-start" flex={1} minW={0}>
          <MetaDefault color="fg.neutral.muted">{titleLabel}</MetaDefault>
          {description ? (
            <BodyDefault color="fg.neutral.default">{description}</BodyDefault>
          ) : null}
          {failedCount > 0 ? (
            <HStack gap={1} alignItems="center" flexWrap="wrap">
              <BodyDefault color="fg.neutral.muted">
                {tActions('importCompleted.downloadFailedRowsPrompt')}
              </BodyDefault>
              <Button
                variant="link"
                size="sm"
                p={0}
                h="auto"
                minH="auto"
                onClick={handleLinkClick}
              >
                {tActions('importCompleted.downloadFailedRowsLink')}
              </Button>
            </HStack>
          ) : null}
          <MonoSmall color="fg.neutral.subtle">{createdAtLabel}</MonoSmall>
        </VStack>
        <HStack gap={2} alignItems="center" flexShrink={0}>
          <Button
            variant="outline"
            size="xs"
            fontWeight="medium"
            onClick={handlePrimaryActionClick}
          >
            <HStack gap={1} alignItems="center" color="fg.neutral.muted">
              <LabelDefault as="span" color="fg.neutral.muted">
                {ctaLabel}
              </LabelDefault>
              <ArrowUpRight01Icon size={16} />
            </HStack>
          </Button>
          <IconButton
            variant="ghost"
            size="xs"
            aria-label={tWidget('dismiss')}
            onClick={handleDismissClick}
          >
            <Cancel01Icon size={16} />
          </IconButton>
        </HStack>
      </HStack>
    );
  }

  return (
    <HStack
      gap={4}
      alignItems="center"
      w="full"
      py={2}
      px={3}
      borderBottomWidth={showBottomBorder ? '1px' : '0'}
      borderBottomStyle="solid"
      borderBottomColor="border.neutral.subtle"
    >
      <VStack gap={1} alignItems="flex-start" flex={1} minW={0}>
        <MetaDefault color="fg.neutral.muted">{titleLabel}</MetaDefault>
        <HStack gap={1} alignItems="center" w="full" minW={0}>
          {severity && severity !== 'Info' ? (
            <Box flexShrink={0}>
              <SeverityIndicator severity={severity} />
            </Box>
          ) : null}
          <Tooltip title={entityLabel}>
            <Box minW={0} flex={1} overflow="hidden">
              <HeadingItem color="fg.neutral.default" truncate>
                {entityLabel}
              </HeadingItem>
            </Box>
          </Tooltip>
        </HStack>
        <MonoSmall color="fg.neutral.subtle">{createdAtLabel}</MonoSmall>
      </VStack>
      <HStack gap={2} alignItems="center" flexShrink={0}>
        <Button
          variant="outline"
          size="xs"
          fontWeight="medium"
          onClick={handlePrimaryActionClick}
        >
          <HStack gap={1} alignItems="center" color="fg.neutral.muted">
            <LabelDefault as="span" color="fg.neutral.muted">
              {ctaLabel}
            </LabelDefault>
            <ArrowUpRight01Icon size={16} />
          </HStack>
        </Button>
        <IconButton
          variant="ghost"
          size="xs"
          aria-label={tWidget('dismiss')}
          onClick={handleDismissClick}
        >
          <Cancel01Icon size={16} />
        </IconButton>
      </HStack>
    </HStack>
  );
}
